import {
  collection,
  getDocs,
  QuerySnapshot,
  DocumentData,
  doc,
  writeBatch,
  query,
  where,
  limit,
  getDoc,
  documentId,
  QueryDocumentSnapshot,
} from "firebase/firestore";
import { db } from "firebaseConfig";

const FIRESTORE_IN_LIMIT = 30;

export const CLIENT_HAS_FORFAITS_ERROR = "ClientHasForfaitsError";

const getClients =  async (): Promise<ClientType[]> =>{
  const querySnapshot: QuerySnapshot<DocumentData> = await getDocs(
    collection(db, "client")
  );
  const rows: ClientType[] = [];

  querySnapshot.forEach((doc) => {
    const clientData = doc.data() as ClientType;
    const clientWithId = { id: doc.id, ...clientData };
    rows.push(clientWithId);
  });

  return rows;
}

//Get one field Client
const getClientField =  async (
  idClient: string,
  field: string
): Promise<string | null> => {
  const docRef = doc(db, "client", idClient);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    return docSnap.data()[field];
  } else {
    // docSnap.data() will be undefined in this case
    console.log("No such document!");
  }
}

//Get Clients with forfaits : une ligne par forfait (un client peut avoir plusieurs forfaits actifs)
const getClientsByForfait =  async (
  idForfaits: CurrentForfaits[]
): Promise<ClientForfaitType[]> => {
  const idclientArray: string[] = Array.from(
    new Set(idForfaits.map((item) => item.idclient).filter(Boolean))
  );
  // Firestore limite une requête "in" à 30 valeurs : on découpe en lots
  const chunks: string[][] = [];
  for (let i = 0; i < idclientArray.length; i += FIRESTORE_IN_LIMIT) {
    chunks.push(idclientArray.slice(i, i + FIRESTORE_IN_LIMIT));
  }
  const snapshots = await Promise.all(
    chunks.map((chunk) =>
      getDocs(query(collection(db, "client"), where(documentId(), "in", chunk)))
    )
  );
  const clientsById = new Map<string, ClientType>();
  snapshots.forEach((querySnapshot) => {
    querySnapshot.docs.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
      clientsById.set(doc.id, { ...(doc.data() as ClientType), id: doc.id });
    });
  });

  const rows: ClientForfaitType[] = [];
  idForfaits.forEach((forfait) => {
    const client = clientsById.get(forfait.idclient);
    if (client) {
      rows.push({
        ...client,
        idforfait: forfait.id,
        site_internet: forfait.site_internet,
        idsites: forfait.idsites,
      });
    }
  });
  return rows.sort(
    (a, b) =>
      (a.raison_sociale || "").localeCompare(b.raison_sociale || "") ||
      (a.site_internet || "").localeCompare(b.site_internet || "")
  );
}

//delete client by id, avec les contacts confirmés par l'utilisateur (dans un même lot).
//Refusé si le client a des forfaits, pour conserver l'historique des interventions.
const deleteClient = async (idClient: string, contactIds: string[], siteIds: string[]) =>{
  const forfaits = await getDocs(
    query(collection(db, "forfait"), where("idclient", "==", idClient), limit(1))
  );
  if (!forfaits.empty) {
    const error = new Error("Ce client a des forfaits : suppression impossible.");
    error.name = CLIENT_HAS_FORFAITS_ERROR;
    throw error;
  }
  const batch = writeBatch(db);
  contactIds.forEach((idContact) => batch.delete(doc(db, "contact", idContact)));
  siteIds.forEach((idSite) => batch.delete(doc(db, "site", idSite)));
  batch.delete(doc(db, "client", idClient));
  await batch.commit();
}

const clientApi = {
  getClients,
  getClientField,
  getClientsByForfait,
  deleteClient,
};

// Fonctions définies une seule fois : référence stable, utilisable dans les dépendances des hooks React
export function useClient() {
  return clientApi;
}